import type { EventFormState, EventRecord } from "./eventPlannerUtils";

export const RECURRENCE_FREQUENCIES = ["daily", "weekly", "monthly", "yearly"] as const;
export const RECURRENCE_END_MODES = ["never", "until", "count"] as const;
export const RECURRENCE_MONTHLY_MODES = ["day_of_month", "ordinal_weekday"] as const;
export const RECURRENCE_ORDINALS = ["first", "second", "third", "fourth", "last"] as const;
export const RECURRENCE_WEEKDAY_KEYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;
export const RECURRENCE_MONTHLY_WEEKDAY_KEYS = [
  ...RECURRENCE_WEEKDAY_KEYS,
  "day",
  "weekday",
  "weekend_day",
] as const;

export type RecurrenceFrequency = (typeof RECURRENCE_FREQUENCIES)[number];
export type RecurrenceEndMode = (typeof RECURRENCE_END_MODES)[number];
export type RecurrenceMonthlyMode = (typeof RECURRENCE_MONTHLY_MODES)[number];
export type RecurrenceOrdinal = (typeof RECURRENCE_ORDINALS)[number];
export type RecurrenceWeekdayKey = (typeof RECURRENCE_WEEKDAY_KEYS)[number];
export type RecurrenceMonthlyWeekdayKey = (typeof RECURRENCE_MONTHLY_WEEKDAY_KEYS)[number];
export type RecurrencePresetKey = "none" | "daily" | "weekly" | "monthly" | "yearly" | "custom";

export type EventRecurrenceSegment = {
  start_date: string;
  start_time: string;
  end_time: string;
  timezone: string;
  frequency: RecurrenceFrequency;
  interval: number;
  end_mode: RecurrenceEndMode;
  until_date: string;
  count: number | null;
  weekly_days: RecurrenceWeekdayKey[];
  monthly_mode: RecurrenceMonthlyMode | "";
  day_of_month: number | null;
  ordinal: RecurrenceOrdinal | "";
  monthly_weekday: RecurrenceMonthlyWeekdayKey | "";
};

export type EventRecurrenceOverride = {
  source_date: string;
  date: string;
  start_time: string;
  end_time: string;
  timezone: string;
};

export type EventRecurrence = {
  segments: EventRecurrenceSegment[];
  overrides: EventRecurrenceOverride[];
};

export type RecurrenceEditorState = {
  frequency: RecurrenceFrequency;
  interval: number;
  end_mode: RecurrenceEndMode;
  until_date: string;
  count: number;
  weekly_days: RecurrenceWeekdayKey[];
  monthly_mode: RecurrenceMonthlyMode;
  day_of_month: number;
  ordinal: RecurrenceOrdinal;
  monthly_weekday: RecurrenceMonthlyWeekdayKey;
};

export type RecurrenceOccurrence = {
  event: EventRecord;
  key: string;
  source_date: string;
  date: string;
  start_time: string;
  end_time: string;
  timezone: string;
  is_override: boolean;
};

type ScheduleLike = Pick<EventFormState, "date" | "start_time" | "end_time" | "timezone">;

const WEEKDAY_INDEX_BY_KEY: Record<RecurrenceWeekdayKey, number> = {
  monday: 0,
  tuesday: 1,
  wednesday: 2,
  thursday: 3,
  friday: 4,
  saturday: 5,
  sunday: 6,
};

const WEEKDAY_LABELS: Record<RecurrenceWeekdayKey, string> = {
  monday: "lundi",
  tuesday: "mardi",
  wednesday: "mercredi",
  thursday: "jeudi",
  friday: "vendredi",
  saturday: "samedi",
  sunday: "dimanche",
};

const MONTHLY_WEEKDAY_LABELS: Record<RecurrenceMonthlyWeekdayKey, string> = {
  monday: "lundi",
  tuesday: "mardi",
  wednesday: "mercredi",
  thursday: "jeudi",
  friday: "vendredi",
  saturday: "samedi",
  sunday: "dimanche",
  day: "jour",
  weekday: "jour de semaine",
  weekend_day: "jour de week-end",
};

const ORDINAL_LABELS: Record<RecurrenceOrdinal, string> = {
  first: "premier",
  second: "deuxieme",
  third: "troisieme",
  fourth: "quatrieme",
  last: "dernier",
};

function padNumber(value: number) {
  return String(value).padStart(2, "0");
}

function parseIsoDate(date: string) {
  const [year, month, day] = String(date || "")
    .split("-")
    .map((part) => Number.parseInt(part, 10));
  if (!year || !month || !day) {
    return null;
  }
  const marker = new Date(Date.UTC(year, month - 1, day));
  return Number.isNaN(marker.getTime()) ? null : marker;
}

function toIsoDate(date: Date) {
  return `${date.getUTCFullYear()}-${padNumber(date.getUTCMonth() + 1)}-${padNumber(date.getUTCDate())}`;
}

function addDays(date: string, days: number) {
  const marker = parseIsoDate(date);
  if (!marker) {
    return date;
  }
  marker.setUTCDate(marker.getUTCDate() + days);
  return toIsoDate(marker);
}

function addMonths(date: string, months: number) {
  const marker = parseIsoDate(date);
  if (!marker) {
    return date;
  }
  const year = marker.getUTCFullYear();
  const monthIndex = marker.getUTCMonth() + months;
  return `${Math.floor(year + monthIndex / 12)}-${padNumber((((monthIndex % 12) + 12) % 12) + 1)}-01`;
}

function addYears(date: string, years: number) {
  const marker = parseIsoDate(date);
  if (!marker) {
    return date;
  }
  return `${marker.getUTCFullYear() + years}-${padNumber(marker.getUTCMonth() + 1)}-${padNumber(marker.getUTCDate())}`;
}

function daysInMonth(year: number, monthIndex: number) {
  return new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
}

function compareOccurrences(left: RecurrenceOccurrence, right: RecurrenceOccurrence) {
  const scheduleCompare = `${left.date} ${left.start_time}`.localeCompare(`${right.date} ${right.start_time}`);
  if (scheduleCompare !== 0) {
    return scheduleCompare;
  }
  return left.key.localeCompare(right.key);
}

function findLastSegmentIndex(segments: EventRecurrenceSegment[], sourceDate: string) {
  for (let index = segments.length - 1; index >= 0; index -= 1) {
    if (segments[index].start_date <= sourceDate) {
      return index;
    }
  }
  return -1;
}

function clampInteger(value: unknown, fallback: number, minValue: number, maxValue: number) {
  const parsed = Number.parseInt(String(value ?? ""), 10);
  if (!Number.isFinite(parsed)) {
    return fallback;
  }
  return Math.min(maxValue, Math.max(minValue, parsed));
}

function weekdayKeyFromDate(date: string): RecurrenceWeekdayKey {
  const marker = parseIsoDate(date);
  const weekdayIndex = marker ? (marker.getUTCDay() + 6) % 7 : 0;
  return RECURRENCE_WEEKDAY_KEYS[weekdayIndex];
}

function normalizeWeeklyDays(rawValue: unknown, fallbackDate: string) {
  if (!Array.isArray(rawValue) || rawValue.length === 0) {
    return [weekdayKeyFromDate(fallbackDate)];
  }
  const normalized = rawValue
    .map((value) => String(value || "").trim().toLowerCase())
    .filter((value): value is RecurrenceWeekdayKey =>
      RECURRENCE_WEEKDAY_KEYS.includes(value as RecurrenceWeekdayKey)
    );
  return normalized.length > 0 ? Array.from(new Set(normalized)) : [weekdayKeyFromDate(fallbackDate)];
}

function normalizeSegment(rawValue: unknown, fallback: ScheduleLike): EventRecurrenceSegment | null {
  if (!rawValue || typeof rawValue !== "object") {
    return null;
  }

  const record = rawValue as Partial<EventRecurrenceSegment>;
  const startDate = String(record.start_date || "").trim() || fallback.date;
  const frequency = RECURRENCE_FREQUENCIES.includes(record.frequency as RecurrenceFrequency)
    ? (record.frequency as RecurrenceFrequency)
    : "daily";
  const endMode = RECURRENCE_END_MODES.includes(record.end_mode as RecurrenceEndMode)
    ? (record.end_mode as RecurrenceEndMode)
    : "never";
  const monthlyMode = RECURRENCE_MONTHLY_MODES.includes(record.monthly_mode as RecurrenceMonthlyMode)
    ? (record.monthly_mode as RecurrenceMonthlyMode)
    : "day_of_month";
  const monthlyWeekday = RECURRENCE_MONTHLY_WEEKDAY_KEYS.includes(record.monthly_weekday as RecurrenceMonthlyWeekdayKey)
    ? (record.monthly_weekday as RecurrenceMonthlyWeekdayKey)
    : "day";
  const ordinal = RECURRENCE_ORDINALS.includes(record.ordinal as RecurrenceOrdinal)
    ? (record.ordinal as RecurrenceOrdinal)
    : "first";

  if (!startDate) {
    return null;
  }

  return {
    start_date: startDate,
    start_time: String(record.start_time ?? fallback.start_time ?? "").trim(),
    end_time: String(record.end_time ?? fallback.end_time ?? "").trim(),
    timezone: String(record.timezone ?? fallback.timezone ?? "").trim(),
    frequency,
    interval: clampInteger(record.interval, 1, 1, 366),
    end_mode: endMode,
    until_date: endMode === "until" ? String(record.until_date || "").trim() : "",
    count: endMode === "count" ? clampInteger(record.count, 1, 1, 500) : null,
    weekly_days: frequency === "weekly" ? normalizeWeeklyDays(record.weekly_days, startDate) : [],
    monthly_mode: frequency === "monthly" ? monthlyMode : "",
    day_of_month:
      frequency === "monthly" && monthlyMode === "day_of_month"
        ? clampInteger(record.day_of_month, Number.parseInt(startDate.slice(-2), 10), 1, 31)
        : null,
    ordinal: frequency === "monthly" && monthlyMode === "ordinal_weekday" ? ordinal : "",
    monthly_weekday: frequency === "monthly" && monthlyMode === "ordinal_weekday" ? monthlyWeekday : "",
  };
}

function normalizeOverride(rawValue: unknown): EventRecurrenceOverride | null {
  if (!rawValue || typeof rawValue !== "object") {
    return null;
  }
  const record = rawValue as Partial<EventRecurrenceOverride>;
  const sourceDate = String(record.source_date || "").trim();
  const date = String(record.date || "").trim();
  if (!sourceDate || !date) {
    return null;
  }
  return {
    source_date: sourceDate,
    date,
    start_time: String(record.start_time || "").trim(),
    end_time: String(record.end_time || "").trim(),
    timezone: String(record.timezone || "").trim(),
  };
}

function sortSegments(segments: EventRecurrenceSegment[]) {
  return [...segments].sort((left, right) => left.start_date.localeCompare(right.start_date));
}

function sortOverrides(overrides: EventRecurrenceOverride[]) {
  return [...overrides].sort((left, right) => {
    const compareSource = left.source_date.localeCompare(right.source_date);
    if (compareSource !== 0) {
      return compareSource;
    }
    return left.date.localeCompare(right.date);
  });
}

function cloneRecurrence(recurrence: EventRecurrence) {
  return normalizeEventRecurrence(recurrence);
}

function buildBaseSegment(anchor: ScheduleLike, frequency: RecurrenceFrequency): EventRecurrenceSegment {
  return {
    start_date: anchor.date,
    start_time: anchor.start_time.trim(),
    end_time: anchor.end_time.trim(),
    timezone: anchor.timezone.trim(),
    frequency,
    interval: 1,
    end_mode: "never",
    until_date: "",
    count: null,
    weekly_days: frequency === "weekly" ? [weekdayKeyFromDate(anchor.date)] : [],
    monthly_mode: frequency === "monthly" ? "day_of_month" : "",
    day_of_month: frequency === "monthly" ? Number.parseInt(anchor.date.slice(-2), 10) || 1 : null,
    ordinal: "",
    monthly_weekday: "",
  };
}

function nthMatchingDayInMonth(
  year: number,
  monthIndex: number,
  ordinal: RecurrenceOrdinal,
  monthlyWeekday: RecurrenceMonthlyWeekdayKey
) {
  const matches: number[] = [];
  const monthLength = daysInMonth(year, monthIndex);

  for (let day = 1; day <= monthLength; day += 1) {
    const marker = new Date(Date.UTC(year, monthIndex, day));
    const weekday = RECURRENCE_WEEKDAY_KEYS[(marker.getUTCDay() + 6) % 7];
    const isWeekday = weekday !== "saturday" && weekday !== "sunday";
    const isWeekend = !isWeekday;

    if (
      monthlyWeekday === "day" ||
      (monthlyWeekday === "weekday" && isWeekday) ||
      (monthlyWeekday === "weekend_day" && isWeekend) ||
      weekday === monthlyWeekday
    ) {
      matches.push(day);
    }
  }

  if (matches.length === 0) {
    return null;
  }

  if (ordinal === "last") {
    return matches[matches.length - 1];
  }

  const index = RECURRENCE_ORDINALS.indexOf(ordinal);
  return matches[index] ?? null;
}

function getMonthlyOccurrenceDate(
  year: number,
  monthIndex: number,
  segment: EventRecurrenceSegment
) {
  if (segment.monthly_mode === "ordinal_weekday") {
    const monthlyDay = nthMatchingDayInMonth(
      year,
      monthIndex,
      (segment.ordinal || "first") as RecurrenceOrdinal,
      (segment.monthly_weekday || "day") as RecurrenceMonthlyWeekdayKey
    );
    return monthlyDay ? `${year}-${padNumber(monthIndex + 1)}-${padNumber(monthlyDay)}` : "";
  }

  const dayOfMonth = segment.day_of_month || 1;
  if (dayOfMonth > daysInMonth(year, monthIndex)) {
    return "";
  }
  return `${year}-${padNumber(monthIndex + 1)}-${padNumber(dayOfMonth)}`;
}

function isDateInSegmentBounds(
  date: string,
  segment: EventRecurrenceSegment,
  nextSegmentStart: string
) {
  if (date < segment.start_date) {
    return false;
  }
  if (nextSegmentStart && date >= nextSegmentStart) {
    return false;
  }
  if (segment.end_mode === "until" && segment.until_date && date > segment.until_date) {
    return false;
  }
  return true;
}

function buildOccurrence(
  event: EventRecord,
  sourceDate: string,
  date: string,
  startTime: string,
  endTime: string,
  timezone: string,
  isOverride: boolean
): RecurrenceOccurrence {
  return {
    event,
    key: `${event.id}:${sourceDate}:${date}:${startTime}:${endTime}:${timezone}`,
    source_date: sourceDate,
    date,
    start_time: startTime,
    end_time: endTime,
    timezone,
    is_override: isOverride,
  };
}

function collectSegmentOccurrences(
  event: EventRecord,
  segment: EventRecurrenceSegment,
  nextSegmentStart: string,
  rangeStart: string,
  rangeEnd: string
) {
  const collected: RecurrenceOccurrence[] = [];
  let occurrencesSeen = 0;

  if (segment.frequency === "daily") {
    let cursor = segment.start_date;
    let iterations = 0;
    while (cursor && cursor <= rangeEnd && iterations < 4000) {
      iterations += 1;
      if (!isDateInSegmentBounds(cursor, segment, nextSegmentStart)) {
        break;
      }
      occurrencesSeen += 1;
      if (segment.end_mode !== "count" || occurrencesSeen <= (segment.count || 0)) {
        if (cursor >= rangeStart) {
          collected.push(buildOccurrence(event, cursor, cursor, segment.start_time, segment.end_time, segment.timezone, false));
        }
      }
      if (segment.end_mode === "count" && occurrencesSeen >= (segment.count || 0)) {
        break;
      }
      cursor = addDays(cursor, segment.interval);
    }
    return collected;
  }

  if (segment.frequency === "weekly") {
    let cursor = segment.start_date;
    let iterations = 0;
    while (cursor && cursor <= rangeEnd && iterations < 5000) {
      iterations += 1;
      if (!isDateInSegmentBounds(cursor, segment, nextSegmentStart)) {
        break;
      }
      const startWeek = parseIsoDate(segment.start_date);
      const currentWeek = parseIsoDate(cursor);
      const weekDelta =
        startWeek && currentWeek ? Math.floor((currentWeek.getTime() - startWeek.getTime()) / (7 * 24 * 60 * 60 * 1000)) : 0;
      const matchesInterval = weekDelta >= 0 && weekDelta % segment.interval === 0;
      const weekday = weekdayKeyFromDate(cursor);
      if (matchesInterval && segment.weekly_days.includes(weekday)) {
        occurrencesSeen += 1;
        if (segment.end_mode !== "count" || occurrencesSeen <= (segment.count || 0)) {
          if (cursor >= rangeStart) {
            collected.push(buildOccurrence(event, cursor, cursor, segment.start_time, segment.end_time, segment.timezone, false));
          }
        }
        if (segment.end_mode === "count" && occurrencesSeen >= (segment.count || 0)) {
          break;
        }
      }
      cursor = addDays(cursor, 1);
    }
    return collected;
  }

  if (segment.frequency === "monthly") {
    let cursor = `${segment.start_date.slice(0, 7)}-01`;
    let iterations = 0;
    while (cursor && cursor <= rangeEnd && iterations < 240) {
      iterations += 1;
      const marker = parseIsoDate(cursor);
      if (!marker) {
        break;
      }
      const occurrenceDate = getMonthlyOccurrenceDate(marker.getUTCFullYear(), marker.getUTCMonth(), segment);
      if (occurrenceDate && isDateInSegmentBounds(occurrenceDate, segment, nextSegmentStart)) {
        occurrencesSeen += 1;
        if (segment.end_mode !== "count" || occurrencesSeen <= (segment.count || 0)) {
          if (occurrenceDate >= rangeStart && occurrenceDate <= rangeEnd) {
            collected.push(
              buildOccurrence(event, occurrenceDate, occurrenceDate, segment.start_time, segment.end_time, segment.timezone, false)
            );
          }
        }
        if (segment.end_mode === "count" && occurrencesSeen >= (segment.count || 0)) {
          break;
        }
      }
      cursor = addMonths(cursor, segment.interval);
    }
    return collected;
  }

  let cursor = segment.start_date;
  let iterations = 0;
  const anchorMarker = parseIsoDate(segment.start_date);
  while (cursor && cursor <= rangeEnd && iterations < 120) {
    iterations += 1;
    const marker = parseIsoDate(cursor);
    if (!marker || !anchorMarker) {
      break;
    }
    const month = anchorMarker.getUTCMonth();
    const day = anchorMarker.getUTCDate();
    const monthLength = daysInMonth(marker.getUTCFullYear(), month);
    const occurrenceDate = day <= monthLength ? `${marker.getUTCFullYear()}-${padNumber(month + 1)}-${padNumber(day)}` : "";
    if (occurrenceDate && isDateInSegmentBounds(occurrenceDate, segment, nextSegmentStart)) {
      occurrencesSeen += 1;
      if (segment.end_mode !== "count" || occurrencesSeen <= (segment.count || 0)) {
        if (occurrenceDate >= rangeStart && occurrenceDate <= rangeEnd) {
          collected.push(buildOccurrence(event, occurrenceDate, occurrenceDate, segment.start_time, segment.end_time, segment.timezone, false));
        }
      }
      if (segment.end_mode === "count" && occurrencesSeen >= (segment.count || 0)) {
        break;
      }
    }
    cursor = addYears(cursor, segment.interval);
  }
  return collected;
}

function countOccurrencesBeforeDate(segment: EventRecurrenceSegment, nextSegmentStart: string, sourceDate: string) {
  const anchorEvent = {
    id: 0,
    internal_name: "",
    display_name: "",
    date: segment.start_date,
    start_time: segment.start_time,
    end_time: segment.end_time,
    publication_status: "draft",
    timezone: segment.timezone,
    location_name: "",
    location_address: "",
    location_street_number: "",
    location_street: "",
    location_postal_code: "",
    location_city: "",
    recurrence: {},
    description: "",
    practical_info: "",
    facebook_url: "",
    revision: 1,
    price: { label: "", amount_cents: null, currency: "EUR" },
    illustration: { kind: "none", reference: "", alt_text: "" },
    connectors: [],
    created_at: null,
    updated_at: null,
  } satisfies EventRecord;
  return collectSegmentOccurrences(anchorEvent, segment, nextSegmentStart, segment.start_date, addDays(sourceDate, -1)).length;
}

function isSimpleSegment(segment: EventRecurrenceSegment, anchorDate: string) {
  if (segment.interval !== 1 || segment.end_mode !== "never") {
    return false;
  }
  if (segment.frequency === "daily" || segment.frequency === "yearly") {
    return true;
  }
  if (segment.frequency === "weekly") {
    return segment.weekly_days.length === 1 && segment.weekly_days[0] === weekdayKeyFromDate(anchorDate);
  }
  return segment.monthly_mode === "day_of_month" && segment.day_of_month === Number.parseInt(anchorDate.slice(-2), 10);
}

function areSchedulesEqual(left: ScheduleLike, right: ScheduleLike) {
  return (
    left.date === right.date &&
    left.start_time === right.start_time &&
    left.end_time === right.end_time &&
    left.timezone === right.timezone
  );
}

function normalizeSchedule(schedule: ScheduleLike): ScheduleLike {
  return {
    date: String(schedule.date || "").trim(),
    start_time: String(schedule.start_time || "").trim(),
    end_time: String(schedule.end_time || "").trim(),
    timezone: String(schedule.timezone || "").trim(),
  };
}

export function normalizeEventRecurrence(rawValue: unknown): EventRecurrence {
  if (!rawValue || typeof rawValue !== "object") {
    return { segments: [], overrides: [] };
  }

  const recurrence = rawValue as Partial<EventRecurrence>;
  const fallbackDate = String(recurrence.segments?.[0]?.start_date || "").trim();
  const fallbackStartTime = String(recurrence.segments?.[0]?.start_time || "").trim();
  const fallbackEndTime = String(recurrence.segments?.[0]?.end_time || "").trim();
  const fallbackTimezone = String(recurrence.segments?.[0]?.timezone || "").trim() || "Europe/Paris";
  const fallback: ScheduleLike = {
    date: fallbackDate,
    start_time: fallbackStartTime,
    end_time: fallbackEndTime,
    timezone: fallbackTimezone,
  };

  const segments = sortSegments(
    (Array.isArray(recurrence.segments) ? recurrence.segments : [])
      .map((segment) => normalizeSegment(segment, fallback))
      .filter((segment): segment is EventRecurrenceSegment => Boolean(segment))
  );
  const overrides = sortOverrides(
    (Array.isArray(recurrence.overrides) ? recurrence.overrides : [])
      .map((override) => normalizeOverride(override))
      .filter((override): override is EventRecurrenceOverride => Boolean(override))
  );

  return segments.length > 0 ? { segments, overrides } : { segments: [], overrides: [] };
}

export function hasEventRecurrence(rawValue: unknown): rawValue is EventRecurrence {
  return normalizeEventRecurrence(rawValue).segments.length > 0;
}

export function createPresetRecurrence(preset: Exclude<RecurrencePresetKey, "none" | "custom">, anchor: ScheduleLike) {
  if (!anchor.date.trim()) {
    return { segments: [], overrides: [] } satisfies EventRecurrence;
  }

  return {
    segments: [buildBaseSegment(anchor, preset)],
    overrides: [],
  } satisfies EventRecurrence;
}

export function detectQuickRecurrencePreset(recurrence: EventRecurrence | unknown, anchorDate: string): RecurrencePresetKey {
  const normalized = normalizeEventRecurrence(recurrence);
  if (normalized.segments.length === 0) {
    return "none";
  }
  if (normalized.segments.length !== 1 || normalized.overrides.length > 0) {
    return "custom";
  }

  const [segment] = normalized.segments;
  if (!isSimpleSegment(segment, anchorDate)) {
    return "custom";
  }
  return segment.frequency;
}

export function buildRecurrenceEditorState(recurrence: EventRecurrence | unknown, anchor: ScheduleLike): RecurrenceEditorState {
  const normalized = normalizeEventRecurrence(recurrence);
  const segment = normalized.segments[0] || buildBaseSegment(anchor, "weekly");
  return {
    frequency: segment.frequency,
    interval: segment.interval,
    end_mode: segment.end_mode,
    until_date: segment.until_date,
    count: segment.count || 10,
    weekly_days: segment.weekly_days.length > 0 ? [...segment.weekly_days] : [weekdayKeyFromDate(anchor.date)],
    monthly_mode: (segment.monthly_mode || "day_of_month") as RecurrenceMonthlyMode,
    day_of_month: segment.day_of_month || clampInteger(anchor.date.slice(-2), 1, 1, 31),
    ordinal: (segment.ordinal || "first") as RecurrenceOrdinal,
    monthly_weekday: (segment.monthly_weekday || "day") as RecurrenceMonthlyWeekdayKey,
  };
}

export function buildRecurrenceFromEditorState(state: RecurrenceEditorState, anchor: ScheduleLike) {
  if (!anchor.date.trim()) {
    return { segments: [], overrides: [] } satisfies EventRecurrence;
  }

  const baseSegment = buildBaseSegment(anchor, state.frequency);
  const segment: EventRecurrenceSegment = {
    ...baseSegment,
    interval: clampInteger(state.interval, 1, 1, 366),
    end_mode: state.end_mode,
    until_date: state.end_mode === "until" ? state.until_date.trim() : "",
    count: state.end_mode === "count" ? clampInteger(state.count, 10, 1, 500) : null,
    weekly_days:
      state.frequency === "weekly"
        ? Array.from(new Set(state.weekly_days.length > 0 ? state.weekly_days : [weekdayKeyFromDate(anchor.date)]))
        : [],
    monthly_mode: state.frequency === "monthly" ? state.monthly_mode : "",
    day_of_month:
      state.frequency === "monthly" && state.monthly_mode === "day_of_month"
        ? clampInteger(state.day_of_month, clampInteger(anchor.date.slice(-2), 1, 1, 31), 1, 31)
        : null,
    ordinal:
      state.frequency === "monthly" && state.monthly_mode === "ordinal_weekday"
        ? (state.ordinal || "first")
        : "",
    monthly_weekday:
      state.frequency === "monthly" && state.monthly_mode === "ordinal_weekday"
        ? (state.monthly_weekday || "day")
        : "",
  };

  return {
    segments: [segment],
    overrides: [],
  } satisfies EventRecurrence;
}

export function syncRecurrenceWithSeriesForm(recurrence: EventRecurrence | unknown, anchor: ScheduleLike) {
  const normalized = normalizeEventRecurrence(recurrence);
  if (normalized.segments.length === 0 || !anchor.date.trim()) {
    return normalized;
  }

  const [firstSegment, ...restSegments] = normalized.segments;
  const nextFirstSegment: EventRecurrenceSegment = {
    ...firstSegment,
    start_date: anchor.date,
    start_time: anchor.start_time.trim(),
    end_time: anchor.end_time.trim(),
    timezone: anchor.timezone.trim(),
  };

  if (nextFirstSegment.frequency === "weekly" && nextFirstSegment.weekly_days.length <= 1) {
    nextFirstSegment.weekly_days = [weekdayKeyFromDate(anchor.date)];
  }
  if (nextFirstSegment.frequency === "monthly") {
    if (nextFirstSegment.monthly_mode === "day_of_month") {
      nextFirstSegment.day_of_month = clampInteger(anchor.date.slice(-2), 1, 1, 31);
    } else {
      const derivedRule = deriveOrdinalWeekdayRule(anchor.date);
      nextFirstSegment.ordinal = derivedRule.ordinal;
      nextFirstSegment.monthly_weekday = derivedRule.monthly_weekday;
    }
  }

  return {
    segments: [nextFirstSegment, ...restSegments],
    overrides: sortOverrides(
      normalized.overrides.filter((override) => override.source_date !== firstSegment.start_date)
    ),
  } satisfies EventRecurrence;
}

export function formatRecurrenceSummary(recurrence: EventRecurrence | unknown) {
  const normalized = normalizeEventRecurrence(recurrence);
  const segment = normalized.segments[0];
  if (!segment) {
    return "Aucune";
  }

  if (normalized.segments.length > 1 || normalized.overrides.length > 0) {
    return "Personnalisee";
  }

  if (segment.frequency === "daily") {
    return segment.interval === 1 ? "Tous les jours" : `Tous les ${segment.interval} jours`;
  }
  if (segment.frequency === "weekly") {
    return segment.interval === 1 ? "Toutes les semaines" : `Toutes les ${segment.interval} semaines`;
  }
  if (segment.frequency === "monthly") {
    if (segment.monthly_mode === "ordinal_weekday") {
      return `Le ${ORDINAL_LABELS[(segment.ordinal || "first") as RecurrenceOrdinal]} ${
        MONTHLY_WEEKDAY_LABELS[(segment.monthly_weekday || "day") as RecurrenceMonthlyWeekdayKey]
      } de chaque mois`;
    }
    return segment.interval === 1 ? "Tous les mois" : `Tous les ${segment.interval} mois`;
  }
  return segment.interval === 1 ? "Tous les ans" : `Tous les ${segment.interval} ans`;
}

export function formatRecurrenceDetail(recurrence: EventRecurrence | unknown) {
  const normalized = normalizeEventRecurrence(recurrence);
  const segment = normalized.segments[0];
  if (!segment) {
    return "";
  }

  if (normalized.segments.length > 1 || normalized.overrides.length > 0) {
    return "Regle personnalisee";
  }

  if (segment.frequency === "weekly" && segment.weekly_days.length > 0) {
    return `Tous les ${segment.weekly_days.map((day) => `${WEEKDAY_LABELS[day]}s`).join(", ")}`;
  }
  if (segment.frequency === "monthly" && segment.monthly_mode === "day_of_month") {
    return `Le ${segment.day_of_month} de chaque mois`;
  }
  return formatRecurrenceSummary(normalized);
}

export function materializeEventOccurrences(
  event: EventRecord,
  options: {
    rangeStart: string;
    rangeEnd: string;
    limit?: number;
  }
) {
  const normalized = normalizeEventRecurrence(event.recurrence);
  const rangeStart = options.rangeStart.trim();
  const rangeEnd = options.rangeEnd.trim();
  if (!rangeStart || !rangeEnd || !event.date.trim() || rangeStart > rangeEnd) {
    return [] as RecurrenceOccurrence[];
  }

  const baseOccurrences =
    normalized.segments.length === 0
      ? event.date >= rangeStart && event.date <= rangeEnd
        ? [buildOccurrence(event, event.date, event.date, event.start_time, event.end_time, event.timezone, false)]
        : []
      : normalized.segments.flatMap((segment, index) =>
          collectSegmentOccurrences(
            event,
            segment,
            normalized.segments[index + 1]?.start_date || "",
            rangeStart,
            rangeEnd
          )
        );

  const overridesBySource = new Map(normalized.overrides.map((override) => [override.source_date, override]));
  const filteredBaseOccurrences = baseOccurrences.filter((occurrence) => !overridesBySource.has(occurrence.source_date));
  const overrideOccurrences = normalized.overrides
    .filter((override) => override.date >= rangeStart && override.date <= rangeEnd)
    .map((override) =>
      buildOccurrence(event, override.source_date, override.date, override.start_time, override.end_time, override.timezone, true)
    );

  const merged = [...filteredBaseOccurrences, ...overrideOccurrences].sort(compareOccurrences);
  return typeof options.limit === "number" ? merged.slice(0, options.limit) : merged;
}

export function resolveOccurrenceContext(
  event: EventRecord,
  occurrenceDate: string,
  sourceDate = ""
) {
  const rangeStart = [sourceDate, occurrenceDate].filter(Boolean).sort()[0] || occurrenceDate;
  const rangeEnd = [sourceDate, occurrenceDate].filter(Boolean).sort().slice(-1)[0] || occurrenceDate;
  if (!rangeStart || !rangeEnd) {
    return null;
  }
  const occurrences = materializeEventOccurrences(event, { rangeStart, rangeEnd });
  return (
    occurrences.find((occurrence) =>
      sourceDate ? occurrence.source_date === sourceDate && occurrence.date === occurrenceDate : occurrence.date === occurrenceDate
    ) || null
  );
}

export function listUpcomingOccurrences(event: EventRecord, fromDate: string, limit = 6) {
  if (!fromDate.trim()) {
    return [] as RecurrenceOccurrence[];
  }
  return materializeEventOccurrences(event, {
    rangeStart: fromDate,
    rangeEnd: addYears(fromDate, 8),
    limit,
  });
}

export function deriveOrdinalWeekdayRule(date: string) {
  const marker = parseIsoDate(date);
  if (!marker) {
    return {
      ordinal: "first" as RecurrenceOrdinal,
      monthly_weekday: "day" as RecurrenceMonthlyWeekdayKey,
    };
  }

  const weekday = weekdayKeyFromDate(date);
  const day = marker.getUTCDate();
  const year = marker.getUTCFullYear();
  const monthIndex = marker.getUTCMonth();
  const monthLength = daysInMonth(year, monthIndex);
  const weekdayMatches = nthMatchingDayInMonth(year, monthIndex, "last", weekday);
  const lastWeekday = nthMatchingDayInMonth(year, monthIndex, "last", "weekday");
  const lastWeekendDay = nthMatchingDayInMonth(year, monthIndex, "last", "weekend_day");

  if (day === monthLength) {
    return { ordinal: "last" as RecurrenceOrdinal, monthly_weekday: "day" as RecurrenceMonthlyWeekdayKey };
  }
  if (day === lastWeekday) {
    return { ordinal: "last" as RecurrenceOrdinal, monthly_weekday: "weekday" as RecurrenceMonthlyWeekdayKey };
  }
  if (day === lastWeekendDay) {
    return { ordinal: "last" as RecurrenceOrdinal, monthly_weekday: "weekend_day" as RecurrenceMonthlyWeekdayKey };
  }
  if (day === weekdayMatches) {
    return { ordinal: "last" as RecurrenceOrdinal, monthly_weekday: weekday };
  }

  const ordinalIndex = Math.floor((day - 1) / 7);
  return {
    ordinal: RECURRENCE_ORDINALS[Math.min(ordinalIndex, 3)],
    monthly_weekday: weekday,
  };
}

export function applyOccurrenceOverrideToEvent(
  event: EventRecord,
  occurrence: RecurrenceOccurrence,
  updatedSchedule: ScheduleLike
) {
  const normalized = cloneRecurrence(normalizeEventRecurrence(event.recurrence));
  const nextSchedule = normalizeSchedule(updatedSchedule);
  const nextOverrides = normalized.overrides.filter((override) => override.source_date !== occurrence.source_date);

  nextOverrides.push({
    source_date: occurrence.source_date,
    date: nextSchedule.date,
    start_time: nextSchedule.start_time,
    end_time: nextSchedule.end_time,
    timezone: nextSchedule.timezone,
  });

  return {
    recurrence: {
      segments: normalized.segments,
      overrides: sortOverrides(nextOverrides),
    } satisfies EventRecurrence,
  };
}

export function applyFutureSplitToEvent(
  event: EventRecord,
  occurrence: RecurrenceOccurrence,
  updatedSchedule: ScheduleLike
) {
  const normalized = cloneRecurrence(normalizeEventRecurrence(event.recurrence));
  const nextSchedule = normalizeSchedule(updatedSchedule);
  if (normalized.segments.length === 0) {
    return {
      date: nextSchedule.date,
      start_time: nextSchedule.start_time,
      end_time: nextSchedule.end_time,
      timezone: nextSchedule.timezone,
      recurrence: { segments: [], overrides: [] } satisfies EventRecurrence,
    };
  }

  const segmentIndex = Math.max(0, findLastSegmentIndex(normalized.segments, occurrence.source_date));
  const currentSegment = normalized.segments[segmentIndex];
  const previousSegments = normalized.segments.slice(0, segmentIndex);
  const nextSegmentStart = normalized.segments[segmentIndex + 1]?.start_date || "";
  const pastOccurrencesCount = countOccurrencesBeforeDate(currentSegment, nextSegmentStart, occurrence.source_date);
  const previousEndDate = addDays(occurrence.source_date, -1);

  const preservedCurrentSegment =
    occurrence.source_date > currentSegment.start_date
      ? {
          ...currentSegment,
          end_mode: "until" as const,
          until_date: previousEndDate,
          count: null,
        }
      : null;

  const rebasedFutureSegment: EventRecurrenceSegment = {
    ...currentSegment,
    start_date: nextSchedule.date,
    start_time: nextSchedule.start_time,
    end_time: nextSchedule.end_time,
    timezone: nextSchedule.timezone,
    until_date: currentSegment.end_mode === "until" ? [currentSegment.until_date, nextSchedule.date].sort().slice(-1)[0] || "" : "",
    count:
      currentSegment.end_mode === "count" && typeof currentSegment.count === "number"
        ? Math.max(1, currentSegment.count - pastOccurrencesCount)
        : null,
  };

  if (rebasedFutureSegment.frequency === "weekly") {
    rebasedFutureSegment.weekly_days = [weekdayKeyFromDate(nextSchedule.date)];
  }
  if (rebasedFutureSegment.frequency === "monthly") {
    if (rebasedFutureSegment.monthly_mode === "day_of_month") {
      rebasedFutureSegment.day_of_month = clampInteger(nextSchedule.date.slice(-2), 1, 1, 31);
    } else {
      const derivedRule = deriveOrdinalWeekdayRule(nextSchedule.date);
      rebasedFutureSegment.ordinal = derivedRule.ordinal;
      rebasedFutureSegment.monthly_weekday = derivedRule.monthly_weekday;
    }
  }

  const nextSegments = [previousSegments, preservedCurrentSegment ? [preservedCurrentSegment] : [], [rebasedFutureSegment]]
    .flat()
    .filter((segment): segment is EventRecurrenceSegment => Boolean(segment));
  const nextOverrides = normalized.overrides.filter((override) => override.source_date < occurrence.source_date);
  const isFirstOccurrence = occurrence.source_date === event.date;

  return {
    date: isFirstOccurrence ? nextSchedule.date : event.date,
    start_time: isFirstOccurrence ? nextSchedule.start_time : event.start_time,
    end_time: isFirstOccurrence ? nextSchedule.end_time : event.end_time,
    timezone: isFirstOccurrence ? nextSchedule.timezone : event.timezone,
    recurrence: {
      segments: sortSegments(nextSegments),
      overrides: sortOverrides(nextOverrides),
    } satisfies EventRecurrence,
  };
}

export function hasOccurrenceScheduleChange(occurrence: RecurrenceOccurrence, form: ScheduleLike) {
  return !areSchedulesEqual(
    {
      date: occurrence.date,
      start_time: occurrence.start_time,
      end_time: occurrence.end_time,
      timezone: occurrence.timezone,
    },
    normalizeSchedule(form)
  );
}

export function weekdayLabel(key: RecurrenceWeekdayKey) {
  return WEEKDAY_LABELS[key];
}

export function monthlyWeekdayLabel(key: RecurrenceMonthlyWeekdayKey) {
  return MONTHLY_WEEKDAY_LABELS[key];
}

export function ordinalLabel(key: RecurrenceOrdinal) {
  return ORDINAL_LABELS[key];
}
